import React, { useEffect, useState } from "react";
import { Box, Popper, ClickAwayListener, Paper, CircularProgress } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import "./HierarchyDropdown.css";
import { PathSummaryItem } from "../utils/hierarchyHelpers";

type DropdownChildProps = {
  onDropdownClose?: () => void;
};

interface HierarchyDropdownProps {
  label: string;
  children: React.ReactElement<DropdownChildProps> | React.ReactElement<DropdownChildProps>[];
  width?: number;
  selectionCount?: number;
  drillDownPath?: Array<{ level: string; value: string }>;
  pathSummary?: PathSummaryItem[];
  disabled?: boolean;
  onBeforeClose?: () => void;
  isLoading?: boolean;
}

const HierarchyDropdown: React.FC<HierarchyDropdownProps> = ({
  label,
  children,
  width = 360,
  selectionCount = 0,
  drillDownPath = [],
  pathSummary = [],
  disabled = false,
  onBeforeClose,
  isLoading = false
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleToggle = (event: React.MouseEvent<HTMLElement>) => {
    if (disabled) {
      return;
    }
    if (anchorEl) {
      console.log('[HierarchyDropdown] Closing dropdown:', { label });
      onBeforeClose?.();
      setAnchorEl(null);
    } else {
      console.log('[HierarchyDropdown] Opening dropdown:', { 
        label, 
        selectionCount, 
        drillDownPath,
        pathSummary 
      });
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    console.log('[HierarchyDropdown] handleClose called:', { label });
    onBeforeClose?.();
    setAnchorEl(null);
  };

  useEffect(() => {
    if (disabled && anchorEl) {
      setAnchorEl(null);
    }
  }, [disabled, anchorEl]);

  return (
    <div className="hierarchy-dropdown-wrapper">
      <button
        className={`hierarchy-dropdown-trigger ${disabled ? "is-disabled" : ""}`}
        onClick={handleToggle}
        disabled={disabled}
      >
        <span className="hierarchy-dropdown-label">{label}</span>
        <span 
          className="hierarchy-dropdown-placeholder" 
          style={{ 
            color: "#9CA3AF",
            fontWeight: 400,
            padding: "0",
            borderRadius: "0",
            maxWidth: "300px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            minHeight: "1em"
          }}
        >
          {/* Empty placeholder to maintain width */}
        </span>
        <KeyboardArrowDownIcon sx={{ fontSize: 18, color: "#9CA3AF" }} />
      </button>
      <Popper
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        placement="bottom-start"
        className="hierarchy-dropdown-popper"
        style={{ width }}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Paper className="hierarchy-dropdown-paper">
            <Box className="hierarchy-dropdown-content" sx={{ position: 'relative' }}>
              {isLoading && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    zIndex: 10,
                  }}
                >
                  <CircularProgress size={24} />
                </Box>
              )}
              {React.Children.map(children, child => {
                if (!React.isValidElement(child)) {
                  return child;
                }
                return React.cloneElement(child as React.ReactElement<DropdownChildProps>, {
                  onDropdownClose: handleClose,
                });
              })}
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </div>
  );
};

export default HierarchyDropdown;

